import React, { useContext, useEffect, useRef, useState, Suspense } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "./useTheme";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import Dashboard from "./Dashboard";
import AuditorySearch from "./SparqlDemo";
import EquipmentSupport from "./EquipmentSupport";
import AuthProvider, { AuthCtx } from "./AuthProvider";
import Login from "./Login";
import Account from "./Account";
import PasswordReset from "./PasswordReset";
import TeacherExport from "./TeacherExport";
import UsersList from "./UsersList";
import Layout from "./Layout";
import LoadingState from "./LoadingState";
import TelegramDissolveLab from "./TelegramDissolveLab";
import TimingGame from "./TimingGame";
import ProfilePage from "./ProfilePage";
import PaymentDone from "./PaymentDone";
import ErrorBoundary from "./ErrorBoundary";
import MaintenanceOverlay from "./MaintenanceOverlay";
import { pruneStaleCache } from "./apiCache";
import { prefetchCurrentSchedule } from "./prefetchSchedule";
import { DailyPaperclipsGlobal } from "./DailyPaperclip";
import { DailyCandyGlobal } from "./DailyCandy";
import { HALLOWEEN_MODE } from "./halloweenMode";
import { getToken } from "./tokenStorage";

const ScheduleWeek = React.lazy(() => import("./ScheduleWeek"));

function QrRewardOverlay() {
  const [anim, setAnim] = useState(null);
  const fired = useRef(false);

  const tryClaim = useRef(async () => {
    const code = sessionStorage.getItem("pendingQrCode");
    if (!code || fired.current) return;
    const token = getToken();
    if (!token) return;
    fired.current = true;
    sessionStorage.removeItem("pendingQrCode");
    try {
      const r = await fetch(`${window.location.origin}/skrep/qr-claim/${code}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) return;
      setAnim({ amount: data.reward_amount ?? 1, type: data.reward_type ?? "regular", already: !!data.already_collected });
    } catch {}
  });

  // On mount: handles reload-after-login case (token already in localStorage)
  useEffect(() => { tryClaim.current(); }, []);

  // On QrRedirect event: handles already-logged-in case
  useEffect(() => {
    const handler = () => tryClaim.current();
    window.addEventListener("pendingQrReady", handler);
    return () => window.removeEventListener("pendingQrReady", handler);
  }, []);

  if (!anim) return null;

  return createPortal(
    <div onClick={() => setAnim(null)} style={{
      position: "fixed", inset: 0, zIndex: 9100,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      background: "oklch(6% 0.015 75 / 0.88)",
      backdropFilter: "blur(24px)",
      cursor: "pointer",
    }}>
      <style>{`
        @keyframes saw-in   { 0%{opacity:0} 100%{opacity:1} }
        @keyframes saw-txt  { 0%{opacity:0;transform:translateY(14px) scale(0.9)} 60%{opacity:1;transform:translateY(-2px) scale(1.02)} 100%{transform:translateY(0) scale(1)} }
        @keyframes saw-ring { 0%{transform:scale(0.5);opacity:0.7} 100%{transform:scale(3.2);opacity:0} }
        @keyframes saw-pop  { 0%{transform:scale(0);opacity:0} 55%{transform:scale(1.22);opacity:1} 75%{transform:scale(0.94)} 100%{transform:scale(1);opacity:1} }
        @keyframes saw-bob  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
        @keyframes saw-glow { 0%,100%{filter:drop-shadow(0 0 18px oklch(75% 0.18 75))} 50%{filter:drop-shadow(0 0 38px oklch(85% 0.22 75))} }
        @keyframes saw-s1{0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-40px,-68px) scale(1.4)}100%{transform:translate(-130px,-154px) scale(0.4);opacity:0}}
        @keyframes saw-s2{0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(42px,-64px) scale(1.5)}100%{transform:translate(138px,-146px) scale(0.4);opacity:0}}
        @keyframes saw-s3{0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-68px,-20px) scale(1.3)}100%{transform:translate(-158px,-34px) scale(0.5);opacity:0}}
        @keyframes saw-s4{0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(70px,-18px) scale(1.4)}100%{transform:translate(160px,-28px) scale(0.4);opacity:0}}
        @keyframes saw-s5{0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(10px,-74px) scale(1.6)}100%{transform:translate(16px,-170px) scale(0.3);opacity:0}}
        @keyframes saw-s6{0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-56px,-50px) scale(1.2)}100%{transform:translate(-120px,-118px) scale(0.6);opacity:0}}
        @keyframes saw-s7{0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(58px,-48px) scale(1.3)}100%{transform:translate(126px,-108px) scale(0.5);opacity:0}}
        @keyframes saw-s8{0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-24px,-68px) scale(1.5)}100%{transform:translate(-40px,-160px) scale(0.4);opacity:0}}
      `}</style>

      <div style={{ position:"absolute", width:200, height:200, borderRadius:"50%", border:"3px solid oklch(75% 0.18 75 / 0.2)", animation:"saw-ring 0.9s cubic-bezier(0.22,1,0.36,1) 0.7s both", pointerEvents:"none" }} />
      <div style={{ position:"absolute", width:200, height:200, borderRadius:"50%", border:"2px solid oklch(85% 0.12 75 / 0.15)", animation:"saw-ring 1.1s cubic-bezier(0.22,1,0.36,1) 0.82s both", pointerEvents:"none" }} />

      {[
        { a:"saw-s1", ch:"✦", size:22, color:"#FCD34D", d:"0.68s" },
        { a:"saw-s2", ch:"📎", size:16, color:"#FCD34D", d:"0.72s" },
        { a:"saw-s3", ch:"✦", size:16, color:"#FDBA74", d:"0.76s" },
        { a:"saw-s4", ch:"★",  size:20, color:"#FCD34D", d:"0.70s" },
        { a:"saw-s5", ch:"✦", size:14, color:"#FDE68A", d:"0.74s" },
        { a:"saw-s6", ch:"★",  size:17, color:"#FDBA74", d:"0.78s" },
        { a:"saw-s7", ch:"📎", size:14, color:"#FCD34D", d:"0.71s" },
        { a:"saw-s8", ch:"✦", size:19, color:"#FDE68A", d:"0.80s" },
      ].map(({ a, ch, size, color, d }, i) => (
        <div key={i} style={{
          position:"absolute", fontSize:size, color, lineHeight:1,
          textShadow:`0 0 12px ${color}88`,
          animation:`${a} 1.4s cubic-bezier(0.22,1,0.36,1) ${d} both`,
          pointerEvents:"none", userSelect:"none",
        }}>{ch}</div>
      ))}

      <img
        src={{ regular: "/paperclip.svg", broken: "/broken_paperclip.svg", golden: "/golden_paperclip.svg" }[anim.type] || "/paperclip.svg"}
        alt=""
        style={{
          width: 96, height: 96, objectFit: "contain",
          animation: "saw-pop 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.15s both, saw-bob 2.6s ease-in-out 1.2s infinite, saw-glow 2.6s ease-in-out 1.2s infinite",
          userSelect: "none", pointerEvents: "none",
        }}
      />

      <div style={{ marginTop:12, animation:"saw-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.42s both", background:"oklch(22% 0.03 75)", border:"1.5px solid oklch(70% 0.18 75 / 0.35)", borderRadius:40, padding:"6px 22px", color:"oklch(88% 0.18 75)", fontWeight:800, fontSize:28, letterSpacing:"-0.5px", pointerEvents:"none" }}>
        {anim.already ? "Уже получено" : `+${anim.amount}`}
      </div>

      <div style={{ marginTop:14, animation:"saw-txt 0.42s cubic-bezier(0.34,1.4,0.64,1) 0.6s both", textAlign:"center", maxWidth:280, padding:"0 20px" }}>
        <p style={{ color:"oklch(96% 0.004 265)", fontSize:20, fontWeight:700, letterSpacing:"-0.3px", margin:0 }}>
          {anim.already ? "Этот QR уже использован" : "Вам начислены скрепки!"}
        </p>
        <p style={{ color:"oklch(35% 0.005 265)", fontSize:11, marginTop:22, letterSpacing:"0.5px", animation:"saw-in 0.5s ease 1.6s both" }}>нажмите чтобы продолжить</p>
      </div>
    </div>,
    document.body
  );
}

function QrRedirect() {
  const { code } = useParams();
  const { user, ready } = useContext(AuthCtx);
  const navigate = useNavigate();

  useEffect(() => {
    if (!ready) return;
    sessionStorage.setItem("pendingQrCode", code);
    if (user) {
      window.dispatchEvent(new Event("pendingQrReady"));
      navigate("/", { replace: true });
    } else {
      navigate("/login", { replace: true });
    }
  }, [ready, user, code, navigate]);

  return null;
}

function StartupTasks() {
  useEffect(() => {
    pruneStaleCache();
    prefetchCurrentSchedule();
  }, []);
  return null;
}

function ThemeApplicator() {
  useTheme();
  return null;
}
function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 639px)").matches;
}

function resolveAuthenticatedStartPath(user) {
  if (isMobileViewport()) return "/schedule";
  if (user?.role === "admin") return "/";
  if (user?.role === "student") return "/schedule";
  return "/auditories";
}

function Protected({ children }) {
  const { user, ready } = useContext(AuthCtx);
  if (!ready) return <LoadingState fullScreen />;
  return user ? children : <Navigate to="/login" replace />;
}

function GuestOnly({ children }) {
  const { user, ready } = useContext(AuthCtx);
  if (!ready) return <LoadingState fullScreen />;
  return user ? <Navigate to={resolveAuthenticatedStartPath(user)} replace /> : children;
}

function ProtectedShell() {
  return (
    <Protected>
      <Layout>
        <Outlet />
      </Layout>
    </Protected>
  );
}

function PublicShell() {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function HomeEntry() {
  const { user, ready } = useContext(AuthCtx);

  if (!ready) return <LoadingState fullScreen />;
  if (isMobileViewport()) {
    return <Navigate to="/schedule" replace />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (user.role === "student") {
    return <Navigate to="/schedule" replace />;
  }
  if (user.role !== "admin") {
    return <Navigate to="/auditories" replace />;
  }
  return <Dashboard />;
}


function Heartbeat() {
  useEffect(() => {
    if (window.__presenceWS && window.__presenceWS.readyState === WebSocket.OPEN) {
      window.dispatchEvent(new CustomEvent("presenceWsReady", { detail: window.__presenceWS }));
    }
  }, []);

  return null;
}
function FallbackRedirect() {
  const { user, ready } = useContext(AuthCtx);

  if (!ready) return <LoadingState fullScreen />;
  if (isMobileViewport()) {
    return <Navigate to="/schedule" replace />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={resolveAuthenticatedStartPath(user)} replace />;
}

function AppRoutes() {
  return (
    <Routes>
        <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
        <Route path="/reset-password" element={<PasswordReset />} />
        <Route path="/qr/:code" element={<QrRedirect />} />
        <Route path="/payment/done" element={<PaymentDone />} />

        <Route element={<PublicShell />}>
          <Route path="/schedule" element={<Suspense fallback={<LoadingState />}><ScheduleWeek /></Suspense>} />
          <Route path="/labs/telegram-dissolve" element={<TelegramDissolveLab />} />
          <Route path="/game" element={<TimingGame />} />
        </Route>

        <Route element={<ProtectedShell />}>
          <Route path="/" element={<HomeEntry />} />
          <Route path="/auditories" element={<AuditorySearch />} />
          <Route path="/support" element={<EquipmentSupport />} />
          <Route path="/account" element={<Account />} />
          <Route path="/teacher-export" element={<TeacherExport />} />
          <Route path="/users" element={<UsersList />} />
          <Route path="/profile/:userId" element={<ProfilePage />} />
        </Route>

        <Route path="*" element={<FallbackRedirect />} />
      </Routes>
  );
}

function RouteAwarePaperclips() {
  const { pathname } = useLocation();
  return (
    <>
      <DailyPaperclipsGlobal key={pathname} route={pathname} />
      {HALLOWEEN_MODE && <DailyCandyGlobal key={"candy-" + pathname} route={pathname} />}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <ThemeApplicator />
        <StartupTasks />
        <Heartbeat />
        <MaintenanceOverlay />
        <QrRewardOverlay />
        <RouteAwarePaperclips />
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </Router>
    </AuthProvider>
  );
}
